import React, { useEffect, useRef, useState } from 'react';
import './Nano.css';

const stats = [
  { end: 127, prefix: '', suffix: '', label: 'tools in the library' },
  { end: 9, prefix: '+', suffix: '', label: 'added this month' },
  { end: 6, prefix: '', suffix: '', label: 'industries served' },
  { end: 63, prefix: '', suffix: '%', label: 'built on reused parts' }
];

const tools = [
  {
    name: 'Renewal generator',
    category: 'Sales',
    status: 'live',
    desc: 'Drafts renewal proposals from live CRM context and pricing rules.',
    effort: 3,
    cost: 2,
    fit: 5,
    type: 'savings',
    metric: '$2.4k/mo',
    reuse: 'Built on 2 shared modules'
  },
  {
    name: 'Support triage',
    category: 'Support',
    status: 'live',
    desc: 'Routes and drafts responses to inbound tickets before a human opens them.',
    effort: 4,
    cost: 3,
    fit: 4,
    type: 'savings',
    metric: '$3.2k/mo',
    reuse: 'Adapted from company #4'
  },
  {
    name: 'Contract parser',
    category: 'Legal',
    status: 'building',
    isNew: true,
    desc: 'Extracts terms, dates and risks from signed agreements into structured data.',
    effort: 3,
    cost: 2,
    fit: 4,
    type: 'savings',
    metric: '$900/mo',
    reuse: '4 days, down from 3 weeks'
  },
  {
    name: 'Pipeline dashboard',
    category: 'RevOps',
    status: 'live',
    desc: 'Replaces the Monday spreadsheet everyone quietly rebuilt by hand.',
    effort: 2,
    cost: 1,
    fit: 3,
    type: 'savings',
    metric: '$1.1k/mo',
    reuse: 'Reused connector core'
  },
  {
    name: 'Onboarding flow',
    category: 'People',
    status: 'building',
    isNew: true,
    desc: 'A capability that did not exist: guided setup shaped to each new hire.',
    effort: 3,
    cost: 2,
    fit: 5,
    type: 'new',
    metric: 'New capability',
    reuse: 'First of its kind'
  },
  {
    name: 'Reporting engine',
    category: 'Finance',
    status: 'live',
    desc: 'Turns raw ledgers into board-ready reporting on a schedule.',
    effort: 4,
    cost: 2,
    fit: 4,
    type: 'new',
    metric: 'New capability',
    reuse: 'Shared core, tailored output'
  },
  {
    name: 'Inventory forecaster',
    category: 'Operations',
    status: 'radar',
    desc: 'Scored and priced on the radar, waiting for the go-ahead to build.',
    effort: 5,
    cost: 3,
    fit: 5,
    type: 'savings',
    metric: '$1.8k/mo',
    reuse: 'Next in the queue'
  },
  {
    name: 'HubSpot connector',
    category: 'Integrations',
    status: 'live',
    desc: 'Written once, dropped into every stack that needs it since.',
    effort: 2,
    cost: 1,
    fit: 2,
    type: 'infra',
    metric: 'Shared infra',
    reuse: 'Reused across 6 companies'
  }
];

const statusLabels = {
  live: 'Live',
  building: 'Building',
  radar: 'On radar'
};

const CountUp = ({ end, prefix = '', suffix = '', run }) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!run) return undefined;
    let raf;
    const duration = 1200;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(end * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, end]);

  return (
    <>
      {prefix}
      {value}
      {suffix}
    </>
  );
};

const Meter = ({ label, value, max = 5, accent }) => (
  <div className="tool-meter">
    <span className="tool-meter-label">{label}</span>
    <span className="tool-meter-pips">
      {Array.from({ length: max }).map((_, i) => (
        <span
          key={i}
          className={`pip ${i < value ? 'pip-on' : ''} ${accent ? 'pip-accent' : ''}`}
        />
      ))}
    </span>
  </div>
);

const Nano = () => {
  const workflows = [
    {
      title: 'Transparent Up Front',
      description: 'Every tool arrives with its build effort, running cost, and the spend it replaces on the table. No black boxes, no surprise invoices.'
    },
    {
      title: 'Every Tool Compounds',
      description: 'A connector written once gets reused, not rebuilt. A detection engine proven in one industry gets adapted, not reinvented. The tenth company gets speed and price that only exist because nine came before it.'
    },
    {
      title: 'Aligned On Savings',
      description: 'When we replace something you were already paying for, we take a share of exactly what we saved you. Nothing more, nothing hidden.'
    }
  ];

  const statsRef = useRef(null);
  const [countersRun, setCountersRun] = useState(false);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCountersRun(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="section engine">
      <div className="container">
        <div className="engine-header">
          <h2 className="section-title">A marketplace that grows with every build</h2>
          <p className="section-subtitle">
            Every tool we ship joins a growing library of internal software. Each one is scoped in the open: its build effort, running cost, and the spend it replaces, all on the table before a line of code is written.
          </p>
        </div>

        <div className="engine-stats" ref={statsRef}>
          {stats.map((stat, index) => (
            <div key={index} className="engine-stat">
              <span className="engine-stat-number">
                <CountUp end={stat.end} prefix={stat.prefix} suffix={stat.suffix} run={countersRun} />
              </span>
              <span className="engine-stat-label">{stat.label}</span>
            </div>
          ))}
        </div>

        <div className="marketplace-grid">
          {tools.map((tool, index) => (
            <article key={index} className={`tool-card status-${tool.status}`}>
              <div className="tool-card-top">
                <span className="tool-category">
                  {tool.isNew && <span className="tool-new">New</span>}
                  {tool.category}
                </span>
                <span className={`tool-status is-${tool.status}`}>
                  <span className="status-dot" />
                  {statusLabels[tool.status]}
                </span>
              </div>

              <h3 className="tool-name">{tool.name}</h3>
              <p className="tool-desc">{tool.desc}</p>

              <div className="tool-meters">
                <Meter label="Build effort" value={tool.effort} />
                <Meter label="Running cost" value={tool.cost} />
                <Meter label="Custom fit" value={tool.fit} accent />
              </div>

              <div className="tool-card-foot">
                <div className={`tool-metric type-${tool.type}`}>
                  <span className="tool-metric-label">
                    {tool.type === 'savings' ? 'Replaces spend' : 'Type'}
                  </span>
                  <span className="tool-metric-value">{tool.metric}</span>
                </div>
                <span className="tool-reuse">{tool.reuse}</span>
              </div>
            </article>
          ))}

          <article className="tool-card tool-card-ghost">
            <div className="ghost-plus">+</div>
            <p className="ghost-text">New tools enter the marketplace every month as the radar fills.</p>
          </article>
        </div>

        <div className="engine-pillars">
          {workflows.map((workflow, index) => (
            <div key={index} className="engine-pillar">
              <h3 className="pillar-title">{workflow.title}</h3>
              <p className="pillar-description">{workflow.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Nano;
